export interface ConcurrencyOutcome<TResult> {
  index: number
  status: 'fulfilled' | 'rejected'
  value?: TResult
  reason?: unknown
}

/**
 * Runs `worker` over `items` with at most `limit` in flight, returning results
 * in INPUT order regardless of completion order.
 *
 * One failure never stops the pool and never rolls the batch back: retiming ten
 * games and having the fourth 500 must still save the other nine. The caller
 * gets a settled outcome per item and decides what to do with each — which is
 * the whole point, because "saved", "rejected" and "we genuinely do not know"
 * need different treatment.
 */
export const runWithConcurrency = async <TItem, TResult>(
  items: TItem[],
  limit: number,
  worker: (item: TItem, index: number) => Promise<TResult>
): Promise<Array<ConcurrencyOutcome<TResult>>> => {
  const results: Array<ConcurrencyOutcome<TResult>> = new Array(items.length)
  const safeLimit = Number.isFinite(limit) && limit >= 1 ? Math.floor(limit) : 1

  let cursor = 0

  const runNext = async (): Promise<void> => {
    while (cursor < items.length) {
      const index = cursor
      cursor += 1
      try {
        results[index] = {
          index,
          status: 'fulfilled',
          value: await worker(items[index], index),
        }
      } catch (reason) {
        results[index] = { index, status: 'rejected', reason }
      }
    }
  }

  const workers = Array.from(
    { length: Math.min(safeLimit, items.length) },
    runNext
  )
  await Promise.all(workers)

  return results
}
